import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

const RARELY_PRODUCT_GALLERY_FIX_MARKER = 'rarely-product-variant-gallery-fix-20260515';

function rarelyImageSignature(url) {
  if (!url) return '';
  try {
    url = new URL(url, window.location.origin).pathname;
  } catch (e) {
    url = String(url).split('?')[0];
  }
  return String(url).split('/').pop().replace(/\.[a-z0-9]+$/i, '').toLowerCase();
}

function rarelyNumberedImageParts(url) {
  const signature = rarelyImageSignature(url);
  const match = signature.match(/^(.*?)(\d+)$/);
  if (!match) return null;
  return { prefix: match[1], number: parseInt(match[2], 10) };
}

function rarelyParseInlineVariants() {
  for (const script of document.scripts) {
    const text = script.textContent || '';
    const start = text.indexOf('var variants = [');
    if (start === -1) continue;
    const jsonStart = text.indexOf('[', start);
    const endMarkers = [';\n      var match', ';\n  function', ';\n  //'];
    let jsonEnd = -1;
    for (const marker of endMarkers) {
      const index = text.indexOf(marker, jsonStart);
      if (index !== -1 && (jsonEnd === -1 || index < jsonEnd)) jsonEnd = index;
    }
    if (jsonStart === -1 || jsonEnd === -1) continue;
    try {
      return JSON.parse(text.slice(jsonStart, jsonEnd));
    } catch (e) {
      return [];
    }
  }
  return [];
}

function rarelyCardMatchesUrl(card, url) {
  const target = rarelyImageSignature(url);
  if (!target) return false;
  const img = card.querySelector('img');
  const values = [
    card.getAttribute('data-image-src'),
    card.getAttribute('data-image-url'),
    img && (img.currentSrc || img.src),
  ];
  return values.some((value) => rarelyImageSignature(value) === target);
}

function rarelyPrioritizeCustomProductGallery(variant, variants) {
  if (!variant?.featured_image?.src) return;
  const imagesContainer = document.getElementById('product-images');
  if (!imagesContainer) return;
  const cards = Array.from(imagesContainer.querySelectorAll('.product-image-card[data-image-url]'));
  if (cards.length < 2) return;

  cards.forEach((card, index) => {
    if (!card.hasAttribute('data-original-gallery-index')) {
      card.setAttribute('data-original-gallery-index', String(index));
    }
  });

  const selectedParts = rarelyNumberedImageParts(variant.featured_image.src);
  const variantStarts = variants
    .filter((item) => item?.featured_image?.src)
    .map((item) => rarelyNumberedImageParts(item.featured_image.src))
    .filter((parts) => parts && selectedParts && parts.prefix === selectedParts.prefix)
    .map((parts) => parts.number)
    .sort((a, b) => a - b);

  let nextVariantNumber = null;
  if (selectedParts) {
    nextVariantNumber = variantStarts.find((number) => number > selectedParts.number) ?? null;
  }

  let groupedCards = [];
  if (selectedParts) {
    groupedCards = cards.filter((card) => {
      const parts = rarelyNumberedImageParts(card.getAttribute('data-image-src') || card.getAttribute('data-image-url'));
      return parts &&
        parts.prefix === selectedParts.prefix &&
        parts.number >= selectedParts.number &&
        (nextVariantNumber === null || parts.number < nextVariantNumber);
    });
  }

  if (groupedCards.length === 0) {
    const featuredCard = cards.find((card) => rarelyCardMatchesUrl(card, variant.featured_image.src));
    if (!featuredCard) return;
    groupedCards = [featuredCard];
  }

  const groupedSet = new Set(groupedCards);
  const orderedCards = groupedCards.concat(cards.filter((card) => !groupedSet.has(card)));
  orderedCards.forEach((card, index) => {
    card.classList.toggle('product-image-card--extra', index >= 4);
    card.setAttribute('data-image-priority-index', String(index));
    imagesContainer.appendChild(card);
  });

  if (window.matchMedia('(max-width: 1024px)').matches) {
    imagesContainer.scrollTo({ left: 0, behavior: 'smooth' });
  }

  document.querySelectorAll('.product-gallery-dot').forEach((dot, index) => {
    dot.classList.toggle('active', index === 0);
  });
}

function rarelyApplySelectedCustomVariantGallery() {
  if (!document.body.classList.contains('template-product')) return;
  const form = document.getElementById('product-form');
  const variantId = form?.querySelector('input[name="id"]')?.value;
  if (!variantId) return;
  const variants = rarelyParseInlineVariants();
  const variant = variants.find((item) => String(item.id) === String(variantId));
  rarelyPrioritizeCustomProductGallery(variant, variants);
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  if (!event.target.closest('.product-options-item')) return;
  window.setTimeout(rarelyApplySelectedCustomVariantGallery, 0);
});

document.addEventListener('DOMContentLoaded', rarelyApplySelectedCustomVariantGallery);
window.RARELY_PRODUCT_GALLERY_FIX_MARKER = RARELY_PRODUCT_GALLERY_FIX_MARKER;
